import { type DeviceInputState } from "@njbsoft/device-input/core";
import { type StagedBlob, type SubmissionDraft } from "@njbsoft/form-composer";
import { type components } from "@njbsoft/types";

const DB_NAME = "njbsoft-test-entry-drafts";
const DB_VERSION = 1;
const STORE_NAME = "drafts";

/**
 * On-disk shape of a staged blob. Blobs are stored as ArrayBuffer + MIME type
 * rather than the Blob object itself: jsdom's Blob implementation is not
 * recognized by the structured-clone algorithm IndexedDB (and fake-indexeddb)
 * use internally, so a raw Blob silently clones to `{}` under the Vitest
 * jsdom test environment. ArrayBuffer is structured-clone-safe everywhere
 * (real browsers included), so this shape round-trips reliably in both.
 */
interface StoredStagedBlob {
  data: ArrayBuffer;
  type: string;
  fileName: string;
  kind: StagedBlob["kind"];
  linkId: string;
  stagedAt: string;
}

interface StoredDraft {
  answers: Record<string, unknown>;
  blobs: Record<string, StoredStagedBlob>;
  updatedAt: number;
}

/** One found device as the store keeps it: the device input as the inspector
 * left it and the New installation request it sends — both plain JSON. */
export interface StoredFoundDevice {
  clientRequestId: string;
  state: DeviceInputState;
  item: components["schemas"]["ChangeRequestItemCreate"];
}

/** An inspection's found devices beside its draft: the client set id they
 * ride under, the entries in order, the rejected submission whose redo they
 * belong to and the one whose returned devices seeded them (each absent on a
 * fresh inspection's, the second also when the seed brought none), and the
 * write's clock reading, which the stale-row sweep ages it by as it ages a
 * draft. */
export interface StoredFoundDevices {
  clientSetId: string;
  entries: StoredFoundDevice[];
  redoOf?: string;
  seededFrom?: string;
  updatedAt: number;
}

/** One violation found on an inspection as the store keeps it: the item it
 * sends and the display names beside it — both plain JSON. */
export interface StoredViolationProposal {
  clientProposalId: string;
  item: components["schemas"]["ViolationProposalCreate"];
  labels: {
    device: string | null;
    deviceSerial: string | null;
    hazardType: string | null;
    tester: string | null;
  };
}

/** An inspection's violation proposals beside its draft: the entries in
 * order (each carries its own client id, so the list has no set id), the
 * rejected submission whose redo they belong to and the one whose returned
 * proposals seeded them (each absent on a fresh inspection's, the second also
 * when the seed brought none), and the write's clock reading, which the
 * stale-row sweep ages it by as it ages a draft. */
export interface StoredViolationProposals {
  entries: StoredViolationProposal[];
  redoOf?: string;
  seededFrom?: string;
  updatedAt: number;
}

async function toStoredBlob(staged: StagedBlob): Promise<StoredStagedBlob> {
  return {
    data: await staged.blob.arrayBuffer(),
    type: staged.blob.type,
    fileName: staged.fileName,
    kind: staged.kind,
    linkId: staged.linkId,
    stagedAt: staged.stagedAt,
  };
}

function fromStoredBlob(stored: StoredStagedBlob): StagedBlob {
  return {
    blob: new Blob([stored.data], { type: stored.type }),
    fileName: stored.fileName,
    kind: stored.kind,
    linkId: stored.linkId,
    stagedAt: stored.stagedAt,
  };
}

async function toStoredDraft(draft: SubmissionDraft): Promise<StoredDraft> {
  const entries = await Promise.all(
    Object.entries(draft.blobs).map(
      async ([clientAttachmentId, staged]) =>
        [clientAttachmentId, await toStoredBlob(staged)] as const,
    ),
  );
  return {
    answers: draft.answers,
    blobs: Object.fromEntries(entries),
    updatedAt: draft.updatedAt,
  };
}

function fromStoredDraft(stored: StoredDraft): SubmissionDraft {
  return {
    answers: stored.answers,
    blobs: Object.fromEntries(
      Object.entries(stored.blobs).map(([clientAttachmentId, stagedBlob]) => [
        clientAttachmentId,
        fromStoredBlob(stagedBlob),
      ]),
    ),
    updatedAt: stored.updatedAt,
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Failed to open the draft-store IndexedDB database"));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    // `request.result` is typed `any` by lib.dom.d.ts; the `<T>` generic here
    // narrows it back to the caller-declared shape of this trusted internal store.
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("draft-store IndexedDB request failed"));
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("draft-store IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("draft-store IndexedDB transaction aborted"));
  });
}

/** The row under `key`, as the caller's trusted internal shape, or null. */
async function getRow<T>(key: string): Promise<T | null> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, "readonly");
    const stored = await requestToPromise<T | undefined>(tx.objectStore(STORE_NAME).get(key));
    await transactionDone(tx);
    return stored ?? null;
  } finally {
    db.close();
  }
}

async function putRow(
  key: string,
  row: StoredDraft | StoredFoundDevices | StoredViolationProposals,
): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(row, key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function deleteRow(key: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

/**
 * Composes the draft key so two users' in-progress drafts on the same
 * assignment never collide. Both components are `encodeURIComponent`-escaped
 * before joining on `:` — otherwise a `:` inside either component could make
 * two distinct (assignmentId, userId) pairs collapse to the same literal key
 * (e.g. `("a:b", "c")` and `("a", "b:c")` would both produce `"a:b:c"`),
 * silently overwriting one user's draft with another's.
 */
export function draftKey(assignmentId: string, userId: string): string {
  return `${encodeURIComponent(assignmentId)}:${encodeURIComponent(userId)}`;
}

/**
 * The key an assignment's found devices are kept under, beside its draft. A
 * draft key holds exactly one unescaped `:` (both components are
 * `encodeURIComponent`-escaped), so the suffixed key can never equal one, nor
 * the violation proposals' key, whose suffix differs.
 */
export function foundDevicesKey(assignmentId: string, userId: string): string {
  return `${draftKey(assignmentId, userId)}:found-devices`;
}

/**
 * The key an assignment's violation proposals are kept under, beside its
 * draft and its found devices: the draft key suffixed `:violation-proposals`,
 * which neither a draft key nor a found-devices key can equal.
 */
export function violationProposalsKey(assignmentId: string, userId: string): string {
  return `${draftKey(assignmentId, userId)}:violation-proposals`;
}

export async function loadDraft(key: string): Promise<SubmissionDraft | null> {
  const stored = await getRow<StoredDraft>(key);
  return stored ? fromStoredDraft(stored) : null;
}

export async function saveDraft(key: string, draft: SubmissionDraft): Promise<void> {
  await putRow(key, await toStoredDraft(draft));
}

export function clearDraft(key: string): Promise<void> {
  return deleteRow(key);
}

export function loadFoundDevices(key: string): Promise<StoredFoundDevices | null> {
  return getRow<StoredFoundDevices>(key);
}

export function saveFoundDevices(key: string, record: StoredFoundDevices): Promise<void> {
  return putRow(key, record);
}

export function clearFoundDevices(key: string): Promise<void> {
  return deleteRow(key);
}

export function loadViolationProposals(key: string): Promise<StoredViolationProposals | null> {
  return getRow<StoredViolationProposals>(key);
}

export function saveViolationProposals(
  key: string,
  record: StoredViolationProposals,
): Promise<void> {
  return putRow(key, record);
}

export function clearViolationProposals(key: string): Promise<void> {
  return deleteRow(key);
}

/** Logout hook — empties the whole draft store regardless of key, so a staged draft (and the found devices and violation proposals beside it) never survives a user switch on a shared device. */
export async function clearAllDrafts(): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).clear();
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

/** Drops every row — a draft, or the found devices or violation proposals beside one — whose updatedAt is older than now - maxAgeMs, so an abandoned draft doesn't linger forever. */
export async function evictStaleDrafts(maxAgeMs: number): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const cutoff = Date.now() - maxAgeMs;
    await new Promise<void>((resolve, reject) => {
      const cursorRequest = store.openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          resolve();
          return;
        }
        // `cursor.value` is typed `any` by lib.dom.d.ts (IDBCursorWithValue);
        // this store only ever writes `StoredDraft`, `StoredFoundDevices` and
        // `StoredViolationProposals` rows, each stamped with `updatedAt`, so
        // the annotation below coerces the trusted internal value back to the
        // member the sweep reads.
        const stored: Pick<
          StoredDraft | StoredFoundDevices | StoredViolationProposals,
          "updatedAt"
        > = cursor.value;
        if (stored.updatedAt < cutoff) {
          cursor.delete();
        }
        cursor.continue();
      };
      cursorRequest.onerror = () =>
        reject(cursorRequest.error ?? new Error("draft-store IndexedDB cursor failed"));
    });
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
